import React, { useMemo, useRef } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { EventPageType } from '@graphql/generated-base';
import type { BoxProps } from '@withjoy/joykit';
import type { LayoutAlohaProps } from '../../LayoutAloha';
import type { PreparedPages } from './Body.constants';
import { pageWidgets } from './Body.constants';
import Page from '../Page';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { SCREENS } from '@shared/types/fontPackScreens';

export interface BodyProps
  extends Readonly<{ readonly page: Maybe<EventPageFragment>; readonly applicatorProps?: object; readonly isWelcomePage?: boolean } & LayoutAlohaProps & BoxProps> {}

export const Body: React.FC<BodyProps> = ({ event, eventHandle, page, applicatorProps = {}, isWelcomePage }) => {
  const pageRef = useRef<HTMLDivElement>(null);

  const onPageEditClick = useEventCallback(() => {
    if (page) {
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editPage',
          inlineEditData: {
            eventHandle,
            pageSlug: page.pageSlug,
            pageType: page.type,
            pageTitle: page.pageTitle,
          },
        },
      });
    }
  });

  const onVisibilityClick = useEventCallback(() => {
    if (page) {
      // send the message to the parent window to open the text editor
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editPageSettings',
          inlineEditData: { pageId: page.id, pageType: page.type, pageTitle: page.pageTitle, pageVisibility: page.visibility, isPageContainer: page.isPageContainer },
        },
      });
    }
  });

  const handleEditFontClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { screen: SCREENS.categoryList } },
    });
  });

  const { isTidbitsPage, isStoryPage, marginTop, isAccommodations } = useMemo(() => {
    return {
      isTidbitsPage: page?.type === 'tidbits',
      isStoryPage: page?.type === 'story',
      marginTop: isWelcomePage ? '0' : '4rem',
      isRegistryPage: page?.type === 'registry',
      isAccommodations: page?.pageSlug === 'accommodations',
    };
  }, [page?.pageSlug, page?.type, isWelcomePage]);

  if (!page) {
    return null;
  }

  const { type } = page;
  const PageWidget = pageWidgets[type as PreparedPages];

  const pageProps = (!isTidbitsPage && {
    title: isWelcomePage ? undefined : page.pageTitle,
    subtitle: !isStoryPage ? page.subTitle : undefined,
    graphicAccent: undefined,
    isAccommodations: isAccommodations,
    isWelcomePage,
    marginTop,
    photo: isWelcomePage ? undefined : page.photoV2,
  }) || {
    title: isWelcomePage ? undefined : page.pageTitle,
    marginTop,
  };
  return PageWidget ? (
    <InlineEditor
      actionData={{
        edit: onPageEditClick,
        visibility: {
          function: onVisibilityClick,
          visibility: page.visibility,
        },
        font: handleEditFontClick,
      }}
      inset={{ x: 4, y: -16 }}
      wrapperType="actionOutside"
      componentName="page"
      elementLabel={page.pageTitle}
      pageName={page.type}
      pageSlug={page.pageSlug}
      stickyOnScroll={true}
      isEligibleForInlineEditing={page?.type !== EventPageType.app}
    >
      <Page
        {...pageProps}
        visibility={page.visibility}
        pageSlug={page.pageSlug}
        ref={pageRef}
        pageId={page.id}
        pageType={page.type}
        isPageContainer={page.isPageContainer}
      >
        <PageWidget
          isAccommodations={isAccommodations}
          isWelcomePage={isWelcomePage}
          event={event}
          eventHandle={eventHandle}
          {...applicatorProps}
          pageId={page.id}
          pageSlug={page.pageSlug}
          pageTitle={pageProps.title}
        />
      </Page>
    </InlineEditor>
  ) : null;
};
